import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  runInInjectionContext,
  Signal,
  SimpleChange,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import isEqual from 'fast-deep-equal';
import { produce } from 'immer';
import {
  animationFrameScheduler,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  observeOn,
  of,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  PropsOf,
  RuntimeEventQueryData,
  TagConfig,
  UpdatePropRequest,
} from '@blaze-runtime/engine-domain';
import { markEventAsHandled } from '@blaze-runtime/engine-util';
import {
  CustomTemplateRendererService,
  replaceAllTokensForRender,
} from '@blaze-runtime/html-template-renderer-feature';
import { createSortingDataAccessor } from '@blaze-shared/datasets-domain';
import {
  ActionsDisplayMode,
  CanvasElementPropertiesWithData,
  ContainerElementMetaUiFeatures,
  DataBindingSourceInfo,
  DEFAULT_ELEMENT_PADDING,
  DEFAULT_NO_DATA_MESSAGE,
  ElementQueryState,
  getBackground,
  getBorderColor,
  getColumnFormatOptions,
  getFormatForField,
  ItemContainerActionsIds,
  ITEMS_CONTAINER_ACTION_ADD,
  ITEMS_CONTAINER_ACTION_EDIT,
  ITEMS_CONTAINER_ACTION_VIEW,
  ITEMS_CONTAINER_ACTION_VIEW_DIALOG,
  ListDirectionMode,
  ListLayoutMode,
  ListScrollMode,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  makeFontStyles,
  PaginatedComponentModel,
  PaginationDirection,
  PropertiesConfigInputColumnModel,
  PropertiesConfigInputContainerItemActionModel,
  PropertiesConfigInputListColumnModel,
  PropertiesConfigInputListCustomFieldModel,
  RuntimeContext,
  RuntimeListCustomFieldModel,
  TableColumnFormat,
} from '@blaze-shared/elements-domain';
import { DatasetRowsService } from '@blaze-shared/schema-data-access';
import {
  BlazeSchema,
  convertBlazeSchemaToElementSchema,
  DatasetRow,
  findColumn,
} from '@blaze-shared/schema-domain';
import { AnyFormatValueOptions } from '@blaze-shared/shared-feature';
import {
  mapOfTemplates,
  PaginatorComponent,
  safeDetectChanges,
  ScrollDetectionMethod,
  ScrollerComponent,
} from '@blaze-shared/shared-ui';
import {
  countedShareReplay,
  hasIcons,
  isDeepEqual,
  isEmptyArray,
  isNullish,
  isNullishOrEmptyString,
  resizeRxUtils,
  setAlphaToHexColor,
  trackByIndex,
  useEmitTracker,
} from '@blaze-shared/shared-util';

import { getHasRelatedElements } from '../../+state/elements/elements.selectors';
import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { arePropsWithData } from '../../canvas-elements/element-model/element-props-guards';
import { matTooltipDefaultsProvider } from '../../providers/mat-tooltip-defaults.provider';
import { ListLikeActionResult } from '../../services/container-actions.service';
import { ElementPaginationService } from '../../services/element-pagination.service';
import { shouldShowEmptyDataMessage } from '../../utils/elements/element.utils';
import { CanvasElementBaseWithActionsComponent } from '../__base/base-with-actions.component';
import { ElementDataActions, OnContainerActionParams } from '../__base/element-data-actions';

import {
  DEFAULT_COLS_COUNT,
  DEFAULT_ROWS_COUNT,
  EXAMPLE_ELEMENT,
  ListFlavor,
  ListImagePropertiesModel,
  ListPropsModel,
  PropertiesConfigInputListConditionalFormatModel,
  ROWS_GAP_PX,
  sortMap,
  SortValues,
} from './list.models';

@UntilDestroy()
@Component({
  selector: 'be-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [matTooltipDefaultsProvider, ElementDataActions<'list'>],
  standalone: false,
  host: {
    '[style.--b-l-radius]': 'elementBorderRadius',
    '[style.--b-l-fillExternal]': 'elementFillExternal',
    '[style.--b-l-border-color]': 'borderColor',
    '[style.--b-l-border-radius]': 'borderRadius',
    '[style.--b-l-element-background]': 'elementBackground',
    '[style.--b-l-internal-padding]': 'internalPadding',
    '[style.--b-l-accent-color]': 'accentColor',
    '[style.--b-l-actions-color]': 'actionsColor',
    '[style.--b-l-heading-color]': 'headingColor',
    '[style.--b-l-text-color]': 'textColor',
    '[style.--b-l-fill-color]': 'fillColor',
    '[style.--b-l-item-hover]': 'itemHoverColor',
    '[style.--b-l-item-select]': 'itemSelectColor',
    '[style.--b-l-indicator-color]': 'indicatorColor',
    '[style.--b-l-indicator-size]': 'indicatorSize',
    '[class.direction-horizontal]': 'isHorizontal',
    '[class.direction-vertical]': 'isVertical',
    '[class.swiping-mode]': 'isSwipingMode',
  },
})
export class ListComponent
  extends CanvasElementBaseWithActionsComponent<'list'>
  implements CanvasElementComponent<'list'>, OnChanges, OnInit, AfterViewInit, OnDestroy
{
  private readonly customTemplateRendererService = inject(CustomTemplateRendererService);
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly elementPaginationService = inject(ElementPaginationService);
  private readonly injector = inject(Injector);

  protected readonly elementDataActions = inject(ElementDataActions);

  @Input() value!: DatasetRow[];
  @Input() queryState$?: Observable<ElementQueryState | undefined>;
  @Input() paginationPending$?: Observable<boolean>;

  @Output() updateProp = new EventEmitter<UpdatePropRequest>();

  @ViewChild('list') listEl!: ElementRef<HTMLDivElement>;
  @ViewChild('listContainer') listContainer!: ElementRef<HTMLDivElement>;
  @ViewChild(ScrollerComponent) blazeScroller?: ScrollerComponent;
  @ViewChild(PaginatorComponent) paginator!: PaginatorComponent;
  @ViewChildren(TemplateRef) tpls!: QueryList<TemplateRef<unknown>>;

  private readonly outputEmitTracker = useEmitTracker();
  private readonly paginationEmitTracker = useEmitTracker();

  private readonly valueBuffer$$ = new Subject<DatasetRow[]>();
  private readonly columns$$ = new BehaviorSubject<PropertiesConfigInputListColumnModel[]>([]);
  private readonly customFields$$ = new BehaviorSubject<
    PropertiesConfigInputListCustomFieldModel[]
  >([]);
  private readonly dataSource$$ = new BehaviorSubject<DataBindingSourceInfo | undefined>(undefined);
  private readonly updateListContainerStyles$$ = new Subject<void>();
  private readonly allActions$$ = new BehaviorSubject<
    PropertiesConfigInputContainerItemActionModel[]
  >([]);

  private readonly sort = new MatSort();
  private readonly commonSortingDataAccessor = createSortingDataAccessor();

  readonly elementActionsKeyRoot = 'b-lac';
  readonly elementDropdownKeyRoot = 'b-ld';
  readonly listKeyRoot = 'b-l';
  readonly scrollData$$ = new BehaviorSubject<{ left: number; width: number } | undefined>(
    undefined,
  );
  readonly markEventAsHandled = markEventAsHandled;
  readonly trackByIndexFn = trackByIndex;

  elementDropdownKey!: string;
  listKey!: string;
  conditionalFormatter$:
    | Observable<
        | ((item: DatasetRow) => PropertiesConfigInputListConditionalFormatModel | undefined)
        | undefined
      >
    | undefined;
  mobilePaginationColor = 'var(--bat-outline)';
  mobilePaginationSelectedColor = 'var(--bat-value)';
  borderColor = 'transparent';
  borderRadius = '0';
  elementBorderRadius = 'var(--bat-radius)';
  elementFillExternal = 'var(--bat-fillExternal)';
  elementBackground = 'transparent';
  internalPadding = DEFAULT_ELEMENT_PADDING;
  accentColor = '#000';
  actionsColor = '#000';
  headingColor = '#000';
  textColor = '#000';
  fillColor = '#000';
  itemHoverColor = '#000';
  itemSelectColor = '#000';
  indicatorEnabled = false;
  indicatorIcon = '';
  indicatorColor = '#000';
  indicatorSize = '24px';
  indicatorAlignH: 'left' | 'right' = 'right';
  indicatorAlignV: 'top' | 'middle' | 'bottom' = 'middle';
  cellsMap!: Record<TableColumnFormat, TemplateRef<unknown>>;
  listDirection!: ListDirectionMode;
  listLayoutMode!: ListLayoutMode;
  listFlavor!: ListFlavor;
  actionsDisplayMode!: ActionsDisplayMode;
  actions: PropertiesConfigInputContainerItemActionModel[][] = [];
  itemAdd: PropertiesConfigInputContainerItemActionModel = {
    id: ITEMS_CONTAINER_ACTION_ADD,
    label: '',
  };
  itemAddEnabled = false;
  itemClickAction: ItemContainerActionsIds | undefined = undefined;
  hasRelatedElements?: Signal<boolean | undefined>;
  actionsGrouped = false;
  displaySearch = true;
  isListEmpty = false;
  noFieldBindingsSetList = false;
  noFieldBindingsSetNotification = false;
  noFieldBindingsSetSubscriptionPlan = false;
  noFieldBindingsSetECommerce = false;
  noFieldBindingsSetCustom = false;
  elementActionsKey!: string;

  // common
  imageField = '';
  // list
  headingField = '';
  headingFieldFormat: TableColumnFormat = 'text';
  headingFieldFormatOptions: AnyFormatValueOptions | undefined;
  descriptionField = '';
  descriptionFieldFormat: TableColumnFormat = 'text';
  descriptionFieldFormatOptions: AnyFormatValueOptions | undefined;
  // notification
  nameField = '';
  messageField = '';
  isUnreadField = '';
  timestampField = '';
  // subscription plan
  titleField = '';
  priceField = '';
  bulletListField = '';
  // e-commerce
  itemNameField = '';
  itemDescriptionField = '';
  itemPriceField = '';
  originalPriceField = '';
  ratingField = '';
  ratingVotesField = '';
  badgeField = '';
  calloutField = '';
  priceColumn?: PropertiesConfigInputColumnModel;
  itemPriceColumn?: PropertiesConfigInputColumnModel;
  originalPriceColumn?: PropertiesConfigInputColumnModel;

  canMarkAsRead = false;
  hasUnreadNotifications = false;

  labelEnabled = false;
  labelText = '';
  icon = '';
  noDataMessage = '';

  valueBuffer: DatasetRow[] = [];
  dataSource = new MatTableDataSource<DatasetRow>([]);
  loadingMoreData = false;
  allDataLoaded = false;
  paginatorTimeout?: ReturnType<typeof setTimeout> = undefined;
  dataConnection$!: Observable<DatasetRow[]>;
  dataRowsCount = 0;
  sortValues: SortValues = { field: '', direction: '' };
  paginationProps: PaginatedComponentModel = {
    state: {
      hasPreviousPage: false,
      hasNextPage: false,
    },
    countOptions: [],
    isServerSide: true,
    enabled: false,
    pageIndex: 0,
    pageSize: 5,
    backwardPaginationSupported: true,
  };
  imageProps?: Partial<ListImagePropertiesModel>;

  /**
   * Used to prevent initial pagination request.
   */
  firstValueArrived = false;

  /**
   * Stores cached information about total data rows.
   * Server returns this value only on first page.
   */
  cachedTotalRowsCount = 0;
  totalRowsChanged = false;

  wrapperStyles: Record<string, string> | null = null;
  listContainerStyles: Record<string, string | undefined> | null = null;
  listStyles!: Record<string, string>;
  elementStyles!: Record<string, string>;
  headingStyles!: Record<string, string>;
  descriptionStyles!: Record<string, string>;
  softStyles!: Record<string, string>;
  actionsContainerStyles!: Record<string, string>;

  tagConfig: TagConfig = [];
  currentMobileListItemPageIndex$: Observable<number> = of(1);

  columns$: Observable<PropertiesConfigInputListColumnModel[]>;
  runtimeCustomFields$: Observable<RuntimeListCustomFieldModel[]>;
  visibleRowCustomFields$: Observable<PropertiesConfigInputListCustomFieldModel[] | undefined>;
  customTemplateCode$!: Observable<string>;

  get isHorizontal(): boolean {
    return this.listDirection === 'horizontal';
  }
  get isVertical(): boolean {
    return this.listDirection === 'vertical';
  }
  get isSwipingMode(): boolean {
    return this.isMobile && this.scrollMode === ListScrollMode.Swipe;
  }

  get isMobileScrollMode(): boolean {
    return this.isMobile && this.scrollMode === ListScrollMode.Scroll;
  }

  get layoutProp(): {
    direction: ListDirectionMode;
    mode: ListLayoutMode;
    cols: number;
    rows: number;
  } {
    const { content } = this.props ?? {};
    const { list } = content ?? {};
    const { layout } = list || {};
    let {
      // eslint-disable-next-line prefer-const
      direction = 'vertical',
      mode = 'vscroll',
      rows = DEFAULT_ROWS_COUNT,
      cols = DEFAULT_COLS_COUNT,
    } = layout || {};

    if (this.elementPaginationService.isMobileListUsingInfinityScroll(this.props)) {
      if (this.scrollMode === 'scroll') {
        mode = 'vscroll';
        cols = 1;
      } else if (this.scrollMode === 'swipe') {
        mode = 'hscroll';
        rows = 1;
      }
    }

    return { direction, mode, rows, cols };
  }

  get hasBoundValue(): boolean {
    return !!this.bindingResults?.['value'];
  }

  get scrollMode(): ListScrollMode {
    return this.props?.content.mobile?.scrollMode ?? ListScrollMode.Swipe;
  }

  get isPaginationHidden(): boolean {
    return (
      this.viewportMode === 'mobile' ||
      this.props?.content?.list?.layout?.mode !== 'paginate' ||
      !this.loadedListLength ||
      this.isSwipingMode
    );
  }

  get shouldUseClientPagination(): boolean {
    return (
      !this.bindingResults?.['value'] ||
      !this.paginationProps.enabled ||
      !this.paginationProps.isServerSide
    );
  }

  get loadedListLength(): number {
    return this.dataSource.data.length || 0;
  }

  get scrollingDetectionMethod(): ScrollDetectionMethod {
    if (
      this.viewportMode === 'mobile' ||
      ['notification', 'subscription-plan'].includes(
        this.props?.content?.list?.layout?.direction ?? '',
      )
    ) {
      return 'intersection';
    }

    return this.props?.content?.list?.layout?.mode === 'hscroll' ? 'horizontal' : 'vertical';
  }

  get isImageFieldSet(): boolean {
    return !!this.imageField || !this.hasBoundValue;
  }

  get isHeadingFieldSet(): boolean {
    return !!this.headingField || !this.hasBoundValue;
  }

  get isDescriptionFieldSet(): boolean {
    return !!this.descriptionField || !this.hasBoundValue;
  }

  get noFieldBindingsSet(): boolean {
    const mapping: Record<ListFlavor, keyof this> = {
      list: 'noFieldBindingsSetList',
      notification: 'noFieldBindingsSetNotification',
      'subscription-plan': 'noFieldBindingsSetSubscriptionPlan',
      'e-commerce': 'noFieldBindingsSetECommerce',
      custom: 'noFieldBindingsSetCustom',
    };
    return this[mapping[this.listFlavor]] as boolean;
  }

  get selection(): SelectionModel<unknown> {
    return this.elementDataActions.selection;
  }

  constructor() {
    super();

    this.initializeConditionalFormatter();

    this.elementDataActions.props = toSignal(this.props$);
    this.elementDataActions.dataValue = this.dataValue;
    this.elementDataActions.elementId = toSignal(this.elementId$);
    this.elementDataActions.elementType = this.elementType;

    this.columns$ = this.columns$$.asObservable();
    this.runtimeCustomFields$ = this.customFields$$.pipe(
      map((fields) =>
        fields.map((field) => ({
          ...field,
          columnName: this.getColumnName(field.column),
        })),
      ),
    );

    this.updateListContainerStyles$$
      .pipe(
        untilDestroyed(this),
        map(() => {
          const height = this.calculateListHeight();

          if (this.isMobile) {
            return null;
          }

          if (this.listLayoutMode === 'fixed') {
            return {
              height: height > -1 ? `${height}px` : 'auto',
            };
          }

          if (this.listLayoutMode === 'vscroll') {
            return {
              height: height > -1 ? `${height}px` : 'auto',
              overflow: height <= 0 ? 'hidden' : 'auto',
            };
          }
          return null;
        }),
      )
      .subscribe((styles) => {
        this.listContainerStyles = styles;
        this.cdr.detectChanges();
      });

    this.createTagConfig(
      this.customFields$$,
      this.columns$$,
      this.createPaletteSchemaObservable(this.dataSource$$),
      this.dataValue.data$,
    ).subscribe((tagConfig) => {
      this.tagConfig = tagConfig;
      this.cdr.detectChanges();
    });

    this.elementDataActions.visibleRowActions$ =
      this.elementDataActions.getVisibleActions<DatasetRow>(
        this.runtimeContext$,
        this.dataSource.connect(),
        this.props$.pipe(
          map((props) =>
            props?.content.unifiedRowActions?.filter((action) => action.label || action.icon),
          ),
          distinctUntilChanged(isEqual),
        ),
        this.element$,
      );

    this.elementDataActions.visibleRowActions$
      .pipe(untilDestroyed(this))
      .subscribe((visibleRowActions) => {
        this.actions = visibleRowActions || [];
      });

    this.elementDataActions.visibleBulkActions$ =
      this.elementDataActions.getVisibleComponentActions(
        this.runtimeContext$,
        this.elementId,
        this.props$.pipe(
          map((props) => props?.content.unifiedBulkActions),
          distinctUntilChanged(isDeepEqual),
        ),
      );

    this.elementDataActions
      .clearActionsNoLongerPending(this.element$)
      .pipe(untilDestroyed(this))
      .subscribe();

    this.visibleRowCustomFields$ = combineLatest({
      context: this.runtimeContext$,
      dataSource: this.dataSource.connect().pipe(distinctUntilChanged(isEqual)),
      customFields: this.runtimeCustomFields$.pipe(
        map((fields) => fields.filter((field) => field.columnName)),
      ),
    }).pipe(
      map(({ context, dataSource, customFields }) => {
        if (!customFields) {
          return;
        }
        // If no custom fields are conditionally visible - show the same custom fields for all rows
        if (!customFields.some((field) => !!field.visibilityExpression)) {
          return new Array(dataSource.length).fill(customFields);
        }

        // Normalization is done here for improved performance - the normalized context is shared across all rows
        const normalizedContext = this.normalizeLinkedParams(context);

        return dataSource.map((rowData: DatasetRow) => {
          // Add normalized row data to the context
          const rowContext = {
            ...normalizedContext,
            output: this.normalizeLinkedParams(rowData as RuntimeContext),
          };

          // Show row custom field if the visibility expression is not provided or if it resolves to true
          return customFields.filter(
            (field) =>
              !field.visibilityExpression ||
              !!this.evaluateExpression(field.visibilityExpression, rowContext, true),
          );
        });
      }),
    );

    this.initMarginHandler((props) => props?.style?.list?.offset?.margin);
    this.initCustomTemplateHandler();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.firstValueArrived && changes['value']) {
      this.firstValueArrived = true;
    }

    if (changes?.['elementId'] && !this.elementActionsKey) {
      this.elementActionsKey = `${this.elementActionsKeyRoot}-${this.elementId}`;
      this.elementDropdownKey = `${this.elementDropdownKeyRoot}-${this.elementId}`;
      this.listKey = `${this.listKeyRoot}-${this.elementId}`;
    }

    if (changes['props']) {
      const { content } = (this.props as ListPropsModel) || {};

      if (this.hasBoundValue) {
        this.imageField = this.getColumnName(content?.fields?.image) || '';
        this.headingField = this.getColumnName(content?.fields?.heading) || '';
        this.headingFieldFormat = getFormatForField(
          this.props?.data,
          'value',
          content?.fields?.heading,
          'text',
        );
        this.headingFieldFormatOptions = getColumnFormatOptions(
          this.props?.data,
          'value',
          content?.fields.description,
        );
        this.descriptionField = this.getColumnName(content?.fields?.description) || '';
        this.descriptionFieldFormat = getFormatForField(
          this.props?.data,
          'value',
          content?.fields?.description,
          'text',
        );
        this.descriptionFieldFormatOptions = getColumnFormatOptions(
          this.props?.data,
          'value',
          content?.fields.description,
        );
        this.nameField = this.getColumnName(content?.fields?.name) || '';
        this.messageField = this.getColumnName(content?.fields?.message) || '';
        this.isUnreadField = this.getColumnName(content?.fields?.isUnread) || '';
        this.timestampField = this.getColumnName(content?.fields?.timestamp) || '';
        this.titleField = this.getColumnName(content?.fields?.title) || '';
        this.priceField = this.getColumnName(content?.fields?.price) || '';
        this.bulletListField = this.getColumnName(content?.fields?.bulletList) || '';
        this.itemNameField = this.getColumnName(content?.fields?.itemName) || '';
        this.itemDescriptionField = this.getColumnName(content?.fields?.itemDescription) || '';
        this.itemPriceField = this.getColumnName(content?.fields?.itemPrice) || '';
        this.originalPriceField = this.getColumnName(content?.fields?.originalPrice) || '';
        this.ratingField = this.getColumnName(content?.fields?.rating) || '';
        this.ratingVotesField = this.getColumnName(content?.fields?.ratingVotes) || '';
        this.badgeField = this.getColumnName(content?.fields?.badge) || '';
        this.calloutField = this.getColumnName(content?.fields?.callout) || '';
        this.priceColumn = findColumn(content?.columns, content?.fields?.price);
        this.itemPriceColumn = findColumn(content?.columns, content?.fields?.itemPrice);
        this.originalPriceColumn = findColumn(content?.columns, content?.fields?.originalPrice);
      } else {
        const layoutTemplate = content?.list?.layout?.direction;

        // Image field based on layout template
        if (layoutTemplate === 'notification') {
          this.imageField = 'imageNotification';
        } else if (layoutTemplate === 'vertical') {
          this.imageField = 'imageVertical';
        } else {
          this.imageField = 'image';
        }

        this.headingField = 'heading';
        this.descriptionField = 'description';
        this.nameField = 'name';
        this.messageField = 'message';
        this.isUnreadField = 'isUnread';
        this.timestampField = 'timestamp';
        this.titleField = 'title';
        this.priceField = 'price';
        this.bulletListField = 'bulletList';
        this.itemNameField = 'itemName';
        this.itemDescriptionField = 'itemDescription';
        this.itemPriceField = 'itemPrice';
        this.itemPriceColumn = {
          name: this.itemPriceField,
          format: 'currency',
          precision: 2,
          currency: 'USD',
        };
        this.originalPriceColumn = {
          name: this.originalPriceField,
          format: 'currency',
          precision: 2,
          currency: 'USD',
        };
      }

      this.itemAddEnabled = content?.itemAdd?.enabled || false;
      this.itemAdd.label = content?.itemAdd?.label || '';
      this.itemAdd.styleSheetId = content?.itemAdd?.styleSheetId;
      this.itemAdd.styleSheetTarget = content?.itemAdd?.styleSheetTarget;
      if (content?.itemView?.enabled) {
        this.itemClickAction = ITEMS_CONTAINER_ACTION_VIEW;
      } else if (content?.rowClick?.enabled) {
        this.itemClickAction = content.rowClick.action;
      } else {
        this.itemClickAction = undefined;
      }

      this.elementDataActions.bulkActions = this.props?.content.unifiedBulkActions ?? [];
      this.elementDataActions.bulkActionsHaveIcons = hasIcons(this.elementDataActions.bulkActions);
      this.elementDataActions.updateSelectionMode(!!this.elementDataActions.bulkActions.length);
      this.elementDataActions.resetSelectionModel();
      this.allActions$$.next([this.itemAdd, ...content.unifiedRowActions]);

      const dataSource = (this.props as CanvasElementPropertiesWithData)?.data?.find(
        (d) => d.key === 'value',
      )?.dataSource;
      this.dataSource$$.next(dataSource);
    }

    if (changes['value'] || changes['props']) {
      const sortItem = Array.isArray(this.props?.content?.sort)
        ? this.props?.content?.sort[0]
        : this.props?.content?.sort;
      const { field, direction } = sortItem || {};

      const sort: SortValues | undefined = changes['props']
        ? field && findColumn(this.props?.content.columns, this.schemaService.getColumnQuery(field)) // NOTE check if sort field exists
          ? { field: this.getColumnName(field) || '', direction: direction ?? 'asc' }
          : { field: '', direction: '' }
        : undefined;

      this.updatePaginationSettings(this.props, changes['props']?.previousValue, sort);

      if (changes['value'] || (this.hasFieldsChanged(changes['props']) && this.hasBoundValue)) {
        this.columns$$.next(this.props?.content?.columns || []);
        this.updateValue(this.value);
      }

      /**
       * Some styles are calculated from values array length
       * and need to be updated every time value changes.
       */
      this.updateStyles();
    }

    if (!this.bindingResults?.['value'] && !this.loadedListLength) {
      this.updateDataSet();
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<ContainerElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe(({ title, columns }) => {
        this.labelText = title ?? '';
        const updatedFields = (this.props?.content?.customFields || []).map((field) => {
          const colProps = findColumn(columns, field.column);
          const result = produce(field, (draft) => {
            if (colProps) {
              draft.label = colProps.name || draft.label;
              draft.displayFormat = colProps.format || draft.displayFormat;
              draft.precision = colProps.precision || draft.precision;
              draft.currency = colProps.currency ?? draft.currency;
              draft.duration = colProps.duration ?? draft.duration;
              draft.dateFormat = colProps.dateFormat ?? draft.dateFormat;
              draft.dateTimeFormat = colProps.dateTimeFormat ?? draft.dateTimeFormat;
            }
          });
          return result;
        });

        this.customFields$$.next(updatedFields);

        // Clear pending action flags when no actions are pending.
        this.store
          .select(runtimeSelectors.selectRowActionPendingUiByElementId(this.elementId))
          .pipe(distinctUntilChanged(isDeepEqual), untilDestroyed(this))
          .subscribe((pendingUi) => {
            if (!pendingUi?.length) {
              this.elementDataActions.clearPendingActions();
            }
          });
        safeDetectChanges(this.cdr);
      });

    /**
     * Declare output schemas when no value is bound to element.
     */
    if (!this.bindingResults?.['value']) {
      this.setDefaultEventSchema('row-select');
      this.setDefaultEventSchema('action');
    }

    /**
     * Declare output schemas when bindings change.
     */
    this.schema$.pipe(untilDestroyed(this)).subscribe((schema: BlazeSchema) => {
      this.setDefaultEventSchema(
        'action',
        convertBlazeSchemaToElementSchema(schema, this.elementId, 'action'),
      );
      this.setDefaultEventSchema(
        'row-select',
        convertBlazeSchemaToElementSchema(schema, this.elementId, 'row-select'),
      );
    });

    const query$ = this.queryState$?.pipe(
      filter((state: ElementQueryState | undefined) => !!state?.initialDataReceived),
      distinctUntilChanged(isEqual),
      countedShareReplay(),
    );

    query$?.pipe(untilDestroyed(this)).subscribe((state: ElementQueryState | undefined) => {
      this.paginationProps = this.elementPaginationService.applyPaginationStateToModel(
        this.paginationProps,
        state,
      );
      this.totalRowsChanged = this.cachedTotalRowsCount !== (state?.totalCount || 0);
      this.cachedTotalRowsCount = state?.totalCount || 0;
      this.cdr.detectChanges();
    });
    query$
      ?.pipe(
        map((state: ElementQueryState | undefined, idx) => ({ pageIndex: state?.pageIndex, idx })),
        filter(({ pageIndex, idx }) => pageIndex === 0 && idx > 0),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.blazeScroller?.scrollTo({ top: 0 });
      });

    runInInjectionContext(this.injector, () => {
      this.hasRelatedElements = toSignal(
        this.store.select(getHasRelatedElements(this.elementId, 'row-select')),
      );
    });

    this.initActionStyles(this.elementActionsKey, this.allActions$$.asObservable(), [
      'efh-action-btn',
      'action-item',
    ]);

    this.stylesCleanup.register([this.listKey]);
  }

  ngAfterViewInit(): void {
    this.cellsMap = mapOfTemplates(this.tpls.toArray(), 'CellTpl');

    resizeRxUtils
      .fromResizeObserver(this.listEl.nativeElement)
      .pipe(untilDestroyed(this))
      .subscribe(() => this.updateListContainerStyles$$.next());

    this.dataConnection$ = this.dataSource.connect().pipe(untilDestroyed(this));
    this.currentMobileListItemPageIndex$ = this.getMobileListPageIndexObservable();

    this.updateLayoutOnResize();
  }

  ngOnDestroy(): void {
    this.dataSource.disconnect();
    this.stylesCleanup.dispose([this.listKey]);
  }

  updateListScroll(event: Event): void {
    const viewport = event.target as HTMLElement;
    this.scrollData$$.next({
      left: viewport.scrollLeft,
      width: viewport.scrollWidth,
    });
  }

  onAddNewRow(event: Event): void {
    if (!this.itemAddEnabled) {
      return;
    }

    this.onAction({ event, action: this.itemAdd, isBulkAction: false, dataRow: {} });
  }

  onRowClick(event: Event, listRow: DatasetRow): void {
    this.updateOutput(listRow);
    if (this.itemClickAction) {
      const label =
        this.itemClickAction === ITEMS_CONTAINER_ACTION_EDIT
          ? 'Edit'
          : this.itemClickAction === ITEMS_CONTAINER_ACTION_VIEW ||
              this.itemClickAction === ITEMS_CONTAINER_ACTION_VIEW_DIALOG
            ? 'View'
            : 'Row click';
      this.onAction({
        event,
        action: { id: this.itemClickAction, label },
        isBulkAction: false,
        dataRow: listRow,
      });
    } else if (this.hasRelatedElements?.()) {
      this.elementDataActions.selection.clear();
    } else if (!this.elementDataActions.singleSelect()) {
      this.elementDataActions.selection.clear();
      this.elementDataActions.toggleSelection(listRow, this.value, {
        select: true,
        allowBulkOutputEmit: true,
      });
      this.cdr.markForCheck();
    } else {
      this.elementDataActions.toggleSelection(listRow, this.value);
      this.cdr.markForCheck();
    }
  }

  onPage(event: PageEvent): void {
    if (!this.paginationProps.isServerSide) {
      return;
    }

    const direction =
      event.pageIndex > (event.previousPageIndex || 0)
        ? PaginationDirection.Forwards
        : PaginationDirection.Backwards;
    this.updatePaginationOutput(direction, event.pageIndex);
  }

  loadMoreData(): void {
    if (
      !this.paginationProps.enabled ||
      !this.paginationProps.isServerSide ||
      this.allDataLoaded ||
      this.loadingMoreData ||
      !this.elementPaginationService.shouldListUseInfinityScroll(
        this.props,
        this.paginationProps.isServerSide,
      )
    ) {
      return;
    }
    this.loadingMoreData = true;
    this.updatePaginationOutput(PaginationDirection.Forwards, this.paginationProps.pageIndex + 1);
  }

  getTemplateFormat(
    format: TableColumnFormat | undefined,
  ): TemplateRef<unknown> | null | undefined {
    return this.cellsMap[format || 'text'];
  }

  /**
   * Checks if a column is a secure column
   * @param columnName column name resolved at runtime (actual column used)
   */
  isFieldSecure(columnName: string): boolean {
    return findColumn(this.dataValue.schema?.columns, columnName)?.isSecure ?? false;
  }

  markNotificationAsRead(event: MouseEvent, item: DatasetRow): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.isUnreadField) {
      console.error('Is unread field not defined');
      return;
    }
    const dataSource = this.getDataSource();
    this.idColumnName = this.idColumnName || this.getIdColumnName(dataSource);
    const rowId = this.idColumnName && item[this.idColumnName];
    if (isNullishOrEmptyString(rowId)) {
      return;
    }
    const datasetId = dataSource?.id && this.schemaService.getDatasetId(dataSource?.id);
    if (!datasetId) {
      throw new Error('unable to retrieve dataset id');
    }
    this.datasetRowsService
      .update(datasetId, { value: { [this.isUnreadField]: false }, rowId })
      .subscribe();
  }

  markAllNotificationsAsRead(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.isUnreadField) {
      console.error('Is unread field not defined');
      return;
    }
    const dataSource = this.getDataSource();
    this.idColumnName = this.idColumnName || this.getIdColumnName(dataSource);
    const ids =
      this.idColumnName && this.valueBuffer.map((item) => item[this.idColumnName || ''] as string);
    if (!ids) {
      return;
    }
    const datasetId = dataSource?.id && this.schemaService.getDatasetId(dataSource?.id);
    if (!datasetId) {
      throw new Error('unable to retrieve dataset id');
    }
    this.datasetRowsService
      .updateBatch(
        datasetId,
        ids.map((id) => ({ rowId: id, value: { [this.isUnreadField]: false }, upsert: false })),
      )
      .subscribe();
  }

  columnTypeClicked(columnType: string, value?: string): void {
    switch (columnType) {
      case 'url':
        return this.dataTypeActions.openPage(value);
      case 'email':
        return this.dataTypeActions.copy(value);
      default:
        return;
    }
  }

  showOriginalPrice(item: DatasetRow): boolean {
    return (
      item[this.priceField] &&
      !(
        typeof item[this.priceField] === 'number' &&
        typeof item[this.originalPriceField] === 'number' &&
        item[this.itemPriceField] >= item[this.originalPriceField]
      )
    );
  }

  onAction(params: OnContainerActionParams): Promise<ListLikeActionResult | undefined> | undefined {
    return this.elementDataActions.onAction({
      ...params,
      output: this.output,
      dataRowSchema: this.getEventSchema('action'),
    });
  }

  getDataSample(): unknown {
    return this.value[0];
  }

  private getClientPaginator(): PaginatorComponent | undefined {
    // Do not use pagination for lists in scroll mode.
    // TODO: [MG] Solve performance issues with virtual scrolling.
    return this.isLayoutMode('hscroll') || this.isLayoutMode('vscroll')
      ? undefined
      : this.paginator;
  }

  private isLayoutMode(mode: ListLayoutMode): boolean {
    return this.layoutProp.mode === mode;
  }

  private hasFieldsChanged(change: SimpleChange | undefined): boolean {
    const currentValue = change?.currentValue as ListPropsModel | undefined;
    const prevValue = change?.previousValue as ListPropsModel | undefined;

    const keys: Array<keyof ListPropsModel['content']> = [
      'columns',
      'fields',
      'customFields',
      'actions',
    ];
    return keys.some((key) => !isEqual(currentValue?.content?.[key], prevValue?.content?.[key]));
  }

  private updatePaginationOutput(direction?: PaginationDirection, index = 0): void {
    const dataSource = this.propsDataSource;
    if (!dataSource) {
      return;
    }

    const data: RuntimeEventQueryData = {
      page: { size: this.paginationProps.pageSize, direction, index },
      elementId: this.elementId,
      dataSource,
    };

    if (this.sortValues.field) {
      data.sort = { columnName: this.getColumnName(this.sortValues.field) };
      if (this.sortValues.direction) {
        data.sort.direction = sortMap[this.sortValues.direction] ?? '';
      }
    }

    if (
      this.firstValueArrived &&
      (this.paginationEmitTracker.shouldEmit(data) || this.totalRowsChanged || !this.allDataLoaded)
    ) {
      this.output.emit({
        eventType: 'query',
        data,
        isInitialData: false,
        output: 'pagination',
      });
    } else {
      this.loadingMoreData = false;
    }
  }

  private updatePaginationSettings(
    currentProps?: PropsOf<'list'>,
    prevProps?: PropsOf<'list'>,
    sort: SortValues = this.sortValues,
  ): void {
    if (!currentProps) {
      return;
    }

    const paginationProps = this.elementPaginationService.getListPaginationProps(currentProps);

    const pageSizeChanged = this.paginationProps.pageSize !== paginationProps.pageSize;
    const modeChanged =
      !!prevProps &&
      currentProps.content.list?.layout.mode !== prevProps?.content.list?.layout.mode;
    const directionChanged =
      !!prevProps &&
      currentProps.content.list?.layout.direction !== prevProps?.content.list?.layout.direction;
    const sortChanged =
      sort?.field !== this.sortValues.field || sort?.direction !== this.sortValues.direction;

    this.paginationProps.enabled = paginationProps.enabled;
    this.paginationProps.pageSize = paginationProps.pageSize;

    this.sortValues = sort || { field: '', direction: '' };
    this.updateSort();

    if (!isNullish(this.paginationProps.state.totalCount)) {
      this.cachedTotalRowsCount = this.paginationProps.state.totalCount;
    }

    if (pageSizeChanged || modeChanged || directionChanged || sortChanged) {
      if (this.listContainer) {
        this.listContainer.nativeElement.scrollTop = 0;
        this.listContainer.nativeElement.scrollLeft = 0;
      }
      this.updatePaginationOutput(PaginationDirection.First);
    }
  }

  private updateValue(value?: any[]): void {
    if (!this.paginationProps.enabled) {
      this.valueBuffer = value || [];
    }

    // Client side pagination
    if (!this.paginationProps.isServerSide) {
      if (this.paginatorTimeout !== undefined) {
        clearTimeout(this.paginatorTimeout);
        this.paginatorTimeout = undefined;
      }

      if (!this.paginator) {
        this.paginatorTimeout = setTimeout(() => {
          this.updateDataSet(value, this.getClientPaginator());
          this.cdr.detectChanges();
        });
      } else {
        this.updateDataSet(value, this.getClientPaginator());
      }
      this.allDataLoaded = true;
    }

    // Server side pagination
    else {
      const usingInfinityScroll = this.elementPaginationService.shouldListUseInfinityScroll(
        this.props,
        this.paginationProps.isServerSide,
      );

      if (this.loadingMoreData) {
        this.valueBuffer = value ? this.valueBuffer.concat(value) : [];
        this.loadingMoreData = false;
      } else if (!usingInfinityScroll || this.paginationProps.pageIndex === 0) {
        this.valueBuffer = value || [];
      }

      // Prevent content jumping when changing pagination mode to fixed page sizes.
      if (this.isLayoutMode('paginate') || this.isLayoutMode('fixed')) {
        this.valueBuffer = this.valueBuffer.slice(0, this.paginationProps.pageSize);
      }

      this.allDataLoaded =
        (value && value.length < this.paginationProps.pageSize) ||
        (this.cachedTotalRowsCount && this.valueBuffer.length === this.cachedTotalRowsCount) ||
        !this.paginationProps.state.hasNextPage;

      this.updateDataSet(this.valueBuffer);
    }
  }

  private updateOutput(data: unknown): void {
    if (this.outputEmitTracker.shouldEmit(data) && this.bindingResults?.['value']) {
      const schema = this.dataValue.schema
        ? convertBlazeSchemaToElementSchema(this.dataValue.schema, this.elementId, 'row-select')
        : undefined;
      this.output.emit({ eventType: 'row-select', data, schema, isInitialData: false });
    }
  }

  private updateDataSet(value?: DatasetRow[], paginator?: PaginatorComponent): void {
    let isListEmpty = false;
    let valueToSet: DatasetRow[] = [];

    if (!this.hasBoundValue) {
      valueToSet = [];
      for (let i = 0; i < 3; i += 1) {
        valueToSet.push(EXAMPLE_ELEMENT);
      }
    } else {
      this.noFieldBindingsSetList =
        [this.isImageFieldSet, this.isHeadingFieldSet, this.isDescriptionFieldSet].every(
          (f) => !f,
        ) && isEmptyArray(this.customFields$$.value);
      this.noFieldBindingsSetNotification =
        [
          this.imageField,
          this.nameField,
          this.messageField,
          this.isUnreadField,
          this.timestampField,
        ].every((f) => !f) && isEmptyArray(this.customFields$$.value);
      this.noFieldBindingsSetSubscriptionPlan =
        [this.imageField, this.titleField, this.priceField, this.bulletListField].every(
          (f) => !f,
        ) && isEmptyArray(this.customFields$$.value);
      this.noFieldBindingsSetECommerce =
        [
          this.imageField,
          this.itemNameField,
          this.itemDescriptionField,
          this.itemPriceField,
          this.originalPriceField,
          this.ratingField,
          this.ratingVotesField,
          this.badgeField,
          this.calloutField,
        ].every((f) => !f) && isEmptyArray(this.customFields$$.value);

      valueToSet = value || [];

      isListEmpty = isEmptyArray(valueToSet);
    }

    this.valueBuffer$$.next(valueToSet);
    this.dataSource.data = valueToSet;
    if (paginator) {
      paginator.pageSize = this.paginationProps.pageSize;
    }
    this.dataSource.paginator = paginator ?? null;

    this.dataSource.sortingDataAccessor = this.commonSortingDataAccessor;
    this.dataSource.sort =
      this.shouldUseClientPagination && this.sortValues.field ? this.sort : null;
    // force sort update
    this.sort.sortChange.emit({ active: this.sort.active, direction: this.sort.direction });

    this.isListEmpty = isListEmpty;
    this.dataRowsCount = value?.length ?? 0;

    this.canMarkAsRead = !!this.isUnreadField;
    this.hasUnreadNotifications =
      !!this.isUnreadField && valueToSet.some((val) => val[this.isUnreadField]);
  }

  private calculateListHeight(): number {
    const { rows, cols } = this.layoutProp;
    const canFit = this.canListFitIntoElement(rows, cols);
    let height = 0;
    if (canFit) {
      return -1;
    }
    if (this.listEl?.nativeElement && this.listEl?.nativeElement.children.length) {
      const firstElement = this.listEl.nativeElement.children[0];
      const firstElementHeight = firstElement.getBoundingClientRect().height;
      height = rows * firstElementHeight + ROWS_GAP_PX * (rows - 1);
    }
    return height;
  }

  private canListFitIntoElement(rows: number, cols: number): boolean {
    if (this.loadedListLength === 0) {
      return false;
    }
    if (this.listDirection === 'vertical') {
      return this.loadedListLength <= cols * rows;
    }
    return this.loadedListLength <= rows;
  }

  private updateSort(): void {
    if (this.sort && this.sortValues.field) {
      this.sort.active = this.getColumnName(this.sortValues.field) || '';
      this.sort.direction = this.sortValues.direction;
    }
  }

  private updateStyles(): void {
    const { content, style } = this.props || {};
    const { actions, searchAndFilters, title, noDataMessage, list: listContent } = content || {};

    const { direction, cols, rows, mode } = this.layoutProp;
    const { general, background, list, image, container } = style || {};
    const { actions: actionsColor, description, heading, accent } = general || {};
    const { radius, outline, outlineWidth, fill } = background || {};
    const { radius: elementRadius, fill: elementFillExternal } = container || {};

    if (
      !actionsColor ||
      !description ||
      !heading ||
      !radius ||
      !outline ||
      !outlineWidth ||
      !fill ||
      !accent ||
      !list ||
      !container ||
      !elementRadius ||
      !elementFillExternal
    ) {
      return;
    }

    const isCustomLayout = direction === 'custom';
    const listLength = this.loadedListLength;
    const listStyles: Record<string, string> = !isCustomLayout
      ? { ...makeFontStyles(style), color: heading.color1 }
      : {};
    const elementStyles: Record<string, string> = !isCustomLayout
      ? { ...makeFontStyles(style), color: heading.color1 }
      : {};
    const headingStyles: Record<string, string> = {};
    const descriptionStyles: Record<string, string> = {};
    const softStyles: Record<string, string> = {};
    const actionsContainerStyles: Record<string, string> = {};
    const labelEnabled = title?.enabled ?? true;

    descriptionStyles['color'] = description.color1;
    softStyles['color'] = setAlphaToHexColor(description.color1, 50);

    if (outline.selectorType !== 'none' && !isCustomLayout) {
      elementStyles['border-color'] = outline.color1;
      elementStyles['border-width'] = outlineWidth;
    } else {
      elementStyles['border'] = 'none';
    }
    if (content?.rowClick?.enabled || content?.itemView?.enabled) {
      elementStyles['cursor'] = 'pointer';
    }

    if (
      direction === 'vertical' ||
      direction === 'subscription-plan' ||
      direction === 'e-commerce' ||
      direction === 'custom'
    ) {
      if (mode === 'hscroll') {
        // Temporary hide list to prevent showing list in 1 column instead of row.
        if (listLength === 0) {
          listStyles['display'] = 'none';
        }

        listStyles['grid-template-columns'] =
          `repeat(${listLength}, calc(${100 / cols}% - ${(ROWS_GAP_PX * (cols - 1)) / cols}px))`;
      }
      if (mode === 'vscroll' || mode === 'full' || mode === 'fixed' || mode === 'paginate') {
        listStyles['grid-template-columns'] = `repeat(${cols}, minmax(100px, 1fr))`;
      }
    }

    if (direction === 'horizontal' && mode === 'hscroll') {
      listStyles['grid-auto-flow'] = 'column';
      listStyles['grid-template-columns'] = `repeat(${Math.ceil(listLength / rows)}, 100%)`;
      listStyles['grid-template-rows'] = `repeat(${rows}, 1fr)`;
    }

    if (actions?.align === 'left') {
      actionsContainerStyles['justify-content'] = 'flex-start';
    }

    const imageProps: Partial<ListImagePropertiesModel> = image ?? {};
    if (!image?.shape) {
      imageProps.shape = 'auto';

      if (direction === 'horizontal') {
        imageProps.radius = radius;
      }

      if (direction === 'e-commerce') {
        imageProps.outline = outline;
        imageProps.outlineWidth = '1px';
        imageProps.radius = radius;
      }
    }
    if (
      direction === 'e-commerce' &&
      imageProps.shape !== 'square' &&
      imageProps.shape !== 'circle'
    ) {
      imageProps.padding = '8px';
    }

    this.internalPadding = list.offset.padding ?? DEFAULT_ELEMENT_PADDING;

    this.elementBorderRadius = elementRadius ?? 'var(--bat-radius)';
    this.elementFillExternal = getBackground(elementFillExternal);
    this.borderColor = getBorderColor(outline);
    this.borderRadius = radius;
    this.accentColor = accent.color1;
    this.actionsColor = actionsColor.color1;
    this.headingColor = heading.color1;
    this.textColor = description.color1;
    this.fillColor = fill.color1;
    this.elementBackground = getBackground(fill);
    this.imageProps = imageProps;
    this.wrapperStyles = {
      ...makeContainerVisualEffectStyles(background?.visualEffect),
      ...makeBorderStyles(container.outline, container.outlineWidth),
    };
    this.listStyles = listStyles;
    this.elementStyles = elementStyles;
    this.headingStyles = headingStyles;
    this.descriptionStyles = descriptionStyles;
    this.softStyles = softStyles;
    this.actionsContainerStyles = actionsContainerStyles;
    this.itemHoverColor = getBackground(list.hover);
    this.itemSelectColor = getBackground(list.selectedRow);

    const indicatorContent = content?.indicator;
    const indicatorStyle = style?.indicator;
    this.indicatorEnabled = !!indicatorContent?.enabled && !!indicatorContent?.icon;
    this.indicatorIcon = indicatorContent?.icon ?? '';
    this.indicatorColor = indicatorStyle?.color?.color1 ?? description.color1;
    this.indicatorSize = indicatorStyle?.size ?? '24px';
    this.indicatorAlignH = indicatorContent?.align?.horizontal === 'left' ? 'left' : 'right';
    this.indicatorAlignV = indicatorContent?.align?.vertical ?? 'middle';

    switch (direction) {
      case 'e-commerce':
        this.listDirection = 'vertical';
        this.listLayoutMode = mode;
        this.listFlavor = 'e-commerce';
        break;
      case 'subscription-plan':
        this.listDirection = 'vertical';
        this.listLayoutMode = 'full';
        this.listFlavor = 'subscription-plan';
        break;
      case 'notification':
        this.listDirection = 'horizontal';
        this.listLayoutMode = 'full';
        this.listFlavor = 'notification';
        break;
      case 'horizontal':
      case 'vertical':
        this.listDirection = direction;
        this.listLayoutMode = mode;
        this.listFlavor = 'list';
        break;
      case 'custom':
        this.listDirection = 'custom';
        this.listLayoutMode = mode;
        this.listFlavor = 'custom';
    }
    this.actionsDisplayMode = actions?.mode ?? 'always';
    this.actionsGrouped = actions?.mode === 'group';
    this.displaySearch = !!searchAndFilters?.searchEnabled;
    this.labelEnabled = labelEnabled;
    this.icon = title?.icon || '';
    this.noDataMessage = shouldShowEmptyDataMessage(this.props)
      ? (noDataMessage ?? DEFAULT_NO_DATA_MESSAGE)
      : '';

    const bulkActionsDisplayMode = listContent?.bulkActionsDisplayMode;
    this.elementDataActions.bulkActionsDisplayMode = bulkActionsDisplayMode ?? 'always';
    this.elementDataActions.bulkActionsGrouped = bulkActionsDisplayMode === 'group';
    this.updateListContainerStyles$$.next();

    const cf = (this.props as ListPropsModel)?.content?.conditionalFormatting;
    let stylesForConditionalFormatting = '';
    if (cf?.enabled) {
      stylesForConditionalFormatting = cf.formats
        .map(
          (format) => `
              .${this.listKey} .element.list-cf-${format.id} {
                ${format.fill?.color1 && format.fill.selectorType !== 'none' ? `background: ${format.fill.color1} !important;` : ''}
                ${format.outline?.color1 ? `border-color: ${format.outline.color1} !important;` : ''}
                ${format.outlineWidth ? `border-width: ${format.outlineWidth} !important; border-style: solid !important;` : ''}
              }
              .${this.listKey} .element.list-cf-${format.id} .heading,
              .${this.listKey} .element.list-cf-${format.id} .name {
                ${format.headline?.color1 ? `color: ${format.headline.color1} !important;` : ''}
                ${format.headlineWeight ? `font-weight: ${format.headlineWeight} !important;` : ''}
              }
              .${this.listKey} .element.list-cf-${format.id} .description {
                ${format.description?.color1 ? `color: ${format.description.color1} !important;` : ''}
              }
            `,
        )
        .join('\n');
    }
    this.stylesCleanup.update(this.listKey, stylesForConditionalFormatting);
  }

  private getDataSource(): DataBindingSourceInfo | undefined {
    return arePropsWithData(this.props)
      ? this.props.data.find((data) => data?.key === 'value')?.dataSource
      : undefined;
  }

  private updateLayoutOnResize(): void {
    this.viewportMode$.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateStyles();
      this.cdr.markForCheck();
    });
  }

  private getMobileListPageIndexObservable(): Observable<number> {
    return combineLatest({
      scroll: this.scrollData$$,
      items: this.dataConnection$,
    }).pipe(
      // Wait for the animation frame to avoid UI jitter when scrolling
      observeOn(animationFrameScheduler),
      distinctUntilChanged(isEqual),
      map(({ scroll, items }) => {
        return scroll ? Math.round((scroll.left * items.length) / scroll.width) : 0;
      }),
      startWith(0),
      countedShareReplay(),
    );
  }

  private initializeConditionalFormatter(): void {
    this.conditionalFormatter$ = combineLatest({
      conditionalFormatting: this.props$.pipe(
        map((props) => (props as ListPropsModel)?.content.conditionalFormatting),
        distinctUntilChanged(isDeepEqual),
      ),
      context: this.runtimeContext$.pipe(distinctUntilChanged(isDeepEqual)),
    }).pipe(
      map(({ conditionalFormatting, context }) => {
        if (!conditionalFormatting?.enabled) {
          return undefined;
        }
        return (item: DatasetRow): PropertiesConfigInputListConditionalFormatModel | undefined =>
          conditionalFormatting.formats.find(
            (format) =>
              !!format.conditions.conditions?.length &&
              this.queryExpression('when', format.conditionsExpression, {
                ...context,
                output: item,
              }),
          );
      }),
    );
  }

  private initCustomTemplateHandler(): void {
    this.customTemplateCode$ = this.props$.pipe(
      map((props) => props?.content?.list?.customTemplate?.code),
      distinctUntilChanged(),
      switchMap((customCode) =>
        this.schema$.pipe(
          map((schema) => this.customTemplateRendererService.createTokens(schema)),
          map((dataTokens) => replaceAllTokensForRender(customCode ?? '', dataTokens)),
        ),
      ),
    );
  }
}
